import {
	AnimatePresence,
	motion,
	useMotionValueEvent,
	useScroll,
} from 'motion/react'
import {
	type ReactNode,
	type Ref,
	type RefObject,
	useCallback,
	useRef,
	useState,
} from 'react'
import { Form, Link, useLocation } from 'react-router'
import { cn } from '#app/utils/misc'
import { siteContent } from '#app/utils/site/content'
import {
	anchorIdFromHref,
	headerScrollState,
} from '#app/utils/site/glide-scroll'
import { useReducedMotionSafe } from '#app/utils/site/scene-clock'
import { type Audience, SITE_LINKS } from '#app/utils/site/site-links'
import { useLandlordSelfSignupOpen } from '#app/utils/user'
import type { SiteViewer } from './SiteShell'
import { Wordmark } from './Wordmark'
import { Container, Pill } from './primitives'

/** The photo hero's element id. Its bottom edge decides "on photo". */
export const HERO_ID = 'tt-hero'
/** Marks the header for the glide offset (`header[data-tt-header]`). */
export const HEADER_SELECTOR = 'header[data-tt-header]'
const MOBILE_NAV_ID = 'tt-mobile-nav'
const TRACK_ID = anchorIdFromHref(SITE_LINKS.howItWorks)
/** The `--ease-tt` curve, for motion. */
const EASE_TT = [0.19, 0.86, 0.28, 1] as const

const FOCUS_RING =
	'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink focus-visible:ring-offset-2'
const BAR_LINK = cn(
	'rounded-sm transition-opacity duration-200 hover:opacity-70',
	FOCUS_RING,
)
const PANEL_ROW = cn(
	'border-b border-tt-line-soft px-1 py-3.5 text-center text-[18px] font-semibold transition-opacity duration-200 hover:opacity-70',
	FOCUS_RING,
)

/**
 * Sticky website header: wordmark, the Tenant / Landlord pill, How it Works,
 * Sign In and Get started. From 880px down the links collapse behind a burger
 * into a slide-down panel. The bar has fixed heights (76px, 64px from 520px
 * down) and the panel overlays the page, so opening it never shifts layout.
 *
 * Two scroll states, read from motion's scroll value (never in render): a
 * hairline once scrolled past 10px, and on the home pages a soft white
 * gradient under the bar while the photo hero is still behind it.
 *
 * Signed-in visitors: a landlord gets a Dashboard pill, a tenant keeps the
 * Get started pill (into the screening flow); both get Log out.
 */
export function SiteHeader({
	audience,
	viewer,
	hasPhotoHero,
}: {
	audience: Audience
	viewer: SiteViewer | null
	hasPhotoHero: boolean
}) {
	const location = useLocation()
	const nav = siteContent.nav
	const homeHref = SITE_LINKS.home[audience]
	const onHome = location.pathname === homeHref
	// TT-197: landlord "Get started" books a demo while sign-up is paused.
	const landlordSignupOpen = useLandlordSelfSignupOpen()
	const primary = primaryLink(audience, viewer, landlordSignupOpen)

	/* Scroll states. The hero rect is read inside the scroll callback only,
	 * and state changes only when a threshold is crossed. */
	const [scrollState, setScrollState] = useState({
		scrolled: false,
		onPhoto: hasPhotoHero,
	})
	const { scrollY } = useScroll()
	useMotionValueEvent(scrollY, 'change', (y) => {
		const hero = hasPhotoHero ? document.getElementById(HERO_ID) : null
		const next = headerScrollState({
			scrollY: y,
			hasPhotoHero,
			heroBottom: hero ? hero.getBoundingClientRect().bottom : null,
			viewportHeight: window.innerHeight,
		})
		setScrollState((prev) =>
			prev.scrolled === next.scrolled && prev.onPhoto === next.onPhoto
				? prev
				: next,
		)
	})

	/* Mobile panel. */
	const [open, setOpen] = useState(false)
	const headerRef = useRef<HTMLElement>(null)
	const burgerRef = useRef<HTMLButtonElement>(null)
	const close = useCallback((refocus: boolean) => {
		setOpen(false)
		if (refocus) burgerRef.current?.focus({ preventScroll: true })
	}, [])

	// Any navigation closes the panel: adjust state during render, no effect.
	const [seenKey, setSeenKey] = useState(location.key)
	if (seenKey !== location.key) {
		setSeenKey(location.key)
		if (open) setOpen(false)
	}

	return (
		<header
			ref={headerRef}
			data-tt-header=""
			data-photo-hero={hasPhotoHero || undefined}
			className={cn(
				'tt-header sticky top-0 z-40 bg-white/[.94] leading-tight backdrop-blur-[10px]',
				scrollState.scrolled && 'tt-header-scrolled',
				scrollState.onPhoto && 'tt-header-on-photo',
			)}
		>
			<Container className="flex h-16 items-center gap-2 min-[361px]:gap-3.5 min-[521px]:h-[76px] min-[881px]:gap-[26px]">
				<Link
					to={homeHref}
					aria-label={nav.logoLabel}
					className={cn(
						'block h-[15px] rounded-sm text-tt-ink min-[361px]:h-[18px] min-[521px]:h-[22px]',
						FOCUS_RING,
					)}
					onClick={() => close(false)}
				>
					<Wordmark title={null} />
				</Link>

				<AudienceToggle audience={audience} />

				<nav
					aria-label="Primary"
					className="ml-auto hidden items-center gap-6 text-[14px] font-medium min-[881px]:flex"
				>
					<HowItWorksLink
						onHome={onHome}
						homeHref={homeHref}
						className={BAR_LINK}
					>
						{nav.howItWorks}
					</HowItWorksLink>
					{viewer ? (
						<Form method="POST" action={SITE_LINKS.logout}>
							<button type="submit" className={cn('font-medium', BAR_LINK)}>
								{nav.logOut}
							</button>
						</Form>
					) : (
						<Link to={SITE_LINKS.signIn[audience]} className={BAR_LINK}>
							{nav.signIn}
						</Link>
					)}
					<Pill href={primary.href} size="sm">
						{primary.label}
					</Pill>
				</nav>

				<SiteMenuButton
					ref={burgerRef}
					open={open}
					onToggle={() => (open ? close(true) : setOpen(true))}
					className="ml-auto min-[881px]:hidden"
				/>
			</Container>

			<SiteMenuPanel
				open={open}
				audience={audience}
				viewer={viewer}
				containerRef={headerRef}
				onClose={close}
				className="min-[881px]:hidden"
			/>
		</header>
	)
}

/** The burger: two bars that cross into an X while the panel is open. The
 * app's NavBar (screening, blog) uses it too, so every page's mobile menu
 * looks the same. The caller decides where it shows (`className`). */
export function SiteMenuButton({
	ref,
	open,
	onToggle,
	className,
}: {
	ref?: Ref<HTMLButtonElement>
	open: boolean
	onToggle: () => void
	className?: string
}) {
	const labels = siteContent.nav.menu
	return (
		<button
			ref={ref}
			type="button"
			className={cn(
				'flex h-[42px] w-9 flex-col items-center justify-center gap-[5px] rounded-xl px-2 min-[361px]:w-[42px] min-[361px]:px-2.5',
				FOCUS_RING,
				className,
			)}
			aria-label={open ? labels.close : labels.open}
			aria-expanded={open}
			aria-controls={open ? MOBILE_NAV_ID : undefined}
			onClick={onToggle}
		>
			<span
				aria-hidden="true"
				className={cn(
					'block h-0.5 w-full rounded bg-tt-ink transition-transform duration-300 ease-tt',
					open && 'translate-y-[3.5px] rotate-45',
				)}
			/>
			<span
				aria-hidden="true"
				className={cn(
					'block h-0.5 w-full rounded bg-tt-ink transition-transform duration-300 ease-tt',
					open && '-translate-y-[3.5px] -rotate-45',
				)}
			/>
		</button>
	)
}

/**
 * The slide-down mobile menu: How it Works, Sign In or Log out, and the
 * primary pill. It overlays the page from the bottom edge of the nearest
 * positioned ancestor, so render it inside the header. Escape, outside clicks
 * and focus leaving `containerRef` (the header) close it. Tabbing past the
 * last row closes it without pulling focus back, so the panel never covers
 * the element that now has focus. Shared with the app's NavBar.
 */
export function SiteMenuPanel({
	open,
	audience,
	viewer,
	containerRef,
	onClose,
	className,
}: {
	open: boolean
	audience: Audience
	viewer: SiteViewer | null
	containerRef: RefObject<HTMLElement | null>
	/** `refocus`: move focus back to the burger (Escape). */
	onClose: (refocus: boolean) => void
	className?: string
}) {
	const location = useLocation()
	const reduced = useReducedMotionSafe()
	const nav = siteContent.nav
	const homeHref = SITE_LINKS.home[audience]
	const onHome = location.pathname === homeHref
	const landlordSignupOpen = useLandlordSelfSignupOpen()
	const primary = primaryLink(audience, viewer, landlordSignupOpen)

	// The listeners live exactly as long as the panel is mounted: the ref
	// callback's cleanup removes them.
	const panelRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (!node) return
			const container = containerRef.current
			const onFocusOut = (event: FocusEvent) => {
				const next = event.relatedTarget
				// No relatedTarget: the window lost focus, or a click on nothing
				// focusable (the pointerdown handler covers that one).
				if (next instanceof Node && !container?.contains(next)) onClose(false)
			}
			const onPointerDown = (event: PointerEvent) => {
				const target = event.target
				if (target instanceof Node && containerRef.current?.contains(target)) {
					return
				}
				onClose(false)
			}
			const onKeyDown = (event: KeyboardEvent) => {
				if (event.key === 'Escape') onClose(true)
			}
			document.addEventListener('pointerdown', onPointerDown)
			document.addEventListener('keydown', onKeyDown)
			container?.addEventListener('focusout', onFocusOut)
			return () => {
				document.removeEventListener('pointerdown', onPointerDown)
				document.removeEventListener('keydown', onKeyDown)
				container?.removeEventListener('focusout', onFocusOut)
			}
		},
		[containerRef, onClose],
	)

	return (
		<AnimatePresence initial={false}>
			{open ? (
				<motion.div
					key="mobile-nav"
					ref={panelRef}
					id={MOBILE_NAV_ID}
					initial={{ height: 0, opacity: 0 }}
					animate={{ height: 'auto', opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={
						reduced ? { duration: 0 } : { duration: 0.3, ease: EASE_TT }
					}
					className={cn(
						'absolute inset-x-0 top-full z-10 overflow-hidden border-b border-tt-line-soft bg-white leading-tight text-tt-ink shadow-[0_24px_40px_-24px_rgba(30,28,27,.25)]',
						className,
					)}
				>
					<nav
						aria-label="Menu"
						className="flex flex-col gap-1 px-[18px] pb-[22px] pt-2"
					>
						<HowItWorksLink
							onHome={onHome}
							homeHref={homeHref}
							className={PANEL_ROW}
							onClick={() => onClose(false)}
						>
							{nav.howItWorks}
						</HowItWorksLink>
						{viewer ? (
							<Form
								method="POST"
								action={SITE_LINKS.logout}
								className="contents"
							>
								<button type="submit" className={PANEL_ROW}>
									{nav.logOut}
								</button>
							</Form>
						) : (
							<Link
								to={SITE_LINKS.signIn[audience]}
								className={PANEL_ROW}
								onClick={() => onClose(false)}
							>
								{nav.signIn}
							</Link>
						)}
						<Pill
							href={primary.href}
							className="mt-3.5 w-full justify-center"
							onClick={() => onClose(false)}
						>
							{primary.label}
						</Pill>
					</nav>
				</motion.div>
			) : null}
		</AnimatePresence>
	)
}

/** Get started for visitors and signed-in tenants (into the screening flow
 * when signed in), Dashboard for signed-in landlords. */
function primaryLink(
	audience: Audience,
	viewer: SiteViewer | null,
	/** TT-197: false while public landlord sign-up is paused → book a demo. */
	landlordSignupOpen: boolean,
) {
	const nav = siteContent.nav
	if (!viewer)
		return {
			label: nav.getStarted,
			href:
				audience === 'landlord' && !landlordSignupOpen
					? SITE_LINKS.demo
					: SITE_LINKS.getStarted[audience],
		}
	return viewer.userType === 'landlord'
		? { label: nav.dashboard, href: SITE_LINKS.landlordDashboard }
		: { label: nav.getStarted, href: SITE_LINKS.tenantApp }
}

/**
 * "How it Works". On a home page it is a plain in-page anchor, which the
 * shell's click delegation turns into a glide. Elsewhere it navigates to the
 * home page and asks it, through navigation state, to glide to the track
 * once mounted (see `landOnMount`).
 */
function HowItWorksLink({
	onHome,
	homeHref,
	className,
	onClick,
	children,
}: {
	onHome: boolean
	homeHref: string
	className?: string
	onClick?: () => void
	children: ReactNode
}) {
	if (onHome) {
		return (
			<a href={SITE_LINKS.howItWorks} className={className} onClick={onClick}>
				{children}
			</a>
		)
	}
	return (
		<Link
			to={homeHref}
			state={{ glideTo: TRACK_ID }}
			className={className}
			onClick={onClick}
		>
			{children}
		</Link>
	)
}

/** The Tenant / Landlord pill. Each side is a real link to its own page. */
export function AudienceToggle({ audience }: { audience: Audience }) {
	const labels = siteContent.nav.audience
	return (
		<nav aria-label={labels.groupLabel} className="flex gap-1">
			{(['tenant', 'landlord'] as const).map((side) => {
				const active = side === audience
				return (
					<Link
						key={side}
						to={SITE_LINKS.home[side]}
						aria-current={active ? 'page' : undefined}
						className={cn(
							'rounded-full px-[9px] py-1.5 text-[12px] font-medium text-tt-body transition-colors duration-200 hover:bg-tt-inset min-[361px]:px-[11px] min-[361px]:py-[7px] min-[361px]:text-[13px] min-[521px]:px-4 min-[521px]:py-2 min-[521px]:text-[14px]',
							active && 'bg-tt-sage font-semibold text-tt-ink hover:bg-tt-sage',
							FOCUS_RING,
						)}
					>
						{labels[side]}
					</Link>
				)
			})}
		</nav>
	)
}
